import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetUsingLocalesListData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetUsingLocalesListState
  extends MergeRequestState<GetUsingLocalesListData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getUsingLocalesList,
  isFetching: (state: ReduxState) =>
    state.server.getUsingLocalesList.isFetching,
  data: (state: ReduxState) => state.server.getUsingLocalesList.data,
  error: (state: ReduxState) => state.server.getUsingLocalesList.error,
};

const initialState: GetUsingLocalesListState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getUsingLocalesList';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  ({ config }: { config?: ServerApiConfig } | void = {}, { rejectWithValue }) =>
    serverApi.getUsingLocalesList(config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetUsingLocalesList {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getUsingLocalesList: ServerGetUsingLocalesList = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
